import { useRecoilValue } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';
import { show as showIntercom } from '@intercom/messenger-js-sdk';

import { usePracticeUsage } from 'lib/hooks/queries/practice/usePracticeUsage';
import { useFeatureWaitlist } from 'lib/hooks/queries/healthcareProgram/useFeatureWaitlist';
import { useJoinFeatureWaitlist } from 'lib/hooks/queries/healthcareProgram/useJoinFeatureWaitlist';
import {
  COMING_SOON_FIELDS,
  ELIGIBILITY_STEP_FIELDS,
  FEATURE_WAITLIST_QUERY_KEY
} from 'lib/constants/healthcareProgram/eligibilitySetup';

import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import InfoBox from 'components/shared/InfoBox/InfoBox';
import Typography from 'components/shared/Typography/Typography';
import { currentPractice } from 'components/practice/practiceState';

import EligibilityRouteToField from '../components/EligibilityRouteToField';

const InfoBanner = ({ children }) => (
  <InfoBox color="primary" className="!rounded-md border-solid border-primary-200 bg-primary-25">
    {children}
  </InfoBox>
);

const DescriptionText = ({ children, ...props }) => (
  <Typography size="small" color="neutral-700" {...props}>
    {children}
  </Typography>
);

const WaitlistCard = ({ featureKey }) => {
  const queryClient = useQueryClient();
  const { data: joinedKeys = [], isLoading: isWaitlistLoading } = useFeatureWaitlist();
  const { mutate: joinWaitlist, isLoading } = useJoinFeatureWaitlist({
    onSuccess: () => queryClient.invalidateQueries([FEATURE_WAITLIST_QUERY_KEY])
  });

  const joined = joinedKeys.includes(featureKey);

  const handleJoin = () => {
    if (!featureKey || joined || isLoading || isWaitlistLoading) return;
    joinWaitlist({ feature_key: featureKey });
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-solid border-neutral-300 bg-violet-25 px-4 py-3">
      <div className="flex flex-col">
        <Typography variant="h6" size="large" weight={800}>
          Be among the first to use MyRelay
        </Typography>
        <Typography size="small" color="neutral-800">
          Implementations are currently scheduled through Q1 2027.
        </Typography>
      </div>
      {joined ? (
        <div className="flex items-center gap-2 rounded-md bg-success-50 px-3 py-1.5">
          <Icon icon="tick-circle-filled" size={16} color="success" />
          <Typography size="small" color="success-700" weight="medium">
            You&apos;re on the waitlist
          </Typography>
        </div>
      ) : (
        <Button
          size="small"
          iconRight="arrow-right-mono"
          className="bg-black hover:bg-neutral-800"
          text="Join the waitlist"
          isLoading={isLoading || isWaitlistLoading}
          onClick={handleJoin}
        />
      )}
    </div>
  );
};

const Check270271Content = () => {
  const { data: usageData } = usePracticeUsage({ serviceKindName: 'eligibility' });
  const usageLimit = Number(usageData?.usage?.usage_limit);
  const hasLimit = Number.isFinite(usageLimit) && usageLimit > 0;

  return (
    <>
      <InfoBanner>
        <div className="flex items-center gap-1">
          {hasLimit && (
            <Typography size="small">
              Your account includes {usageLimit.toLocaleString()} checks/month.
            </Typography>
          )}
          <Button
            className="h-auto"
            variant="link"
            text="Contact us"
            textClassName="font-400 underline"
            onClick={() => showIntercom()}
          />
          <Typography size="small">to increase your limit.</Typography>
        </div>
      </InfoBanner>
      <DescriptionText>
        Real-time HIPAA 270/271 eligibility transactions sent directly to your primary
        clearinghouse. Returns benefit data including deductible, copay, coinsurance, and
        out-of-pocket max.
      </DescriptionText>
    </>
  );
};

// Biller queue is MedBill-only, so non-MedBill practices get team-member-only
// copy (the queue radio is hidden in EligibilityRouteToField).
const AlertBillerContent = ({ step, onUpdate }) => {
  const practice = useRecoilValue(currentPractice);
  const isMedbillClient = !!practice?.medbill_client;

  return (
    <>
      <InfoBanner>
        <Typography size="small">
          {isMedbillClient
            ? 'Route unresolved eligibility to a team member or your built-in Myriad biller queue.'
            : 'Route unresolved eligibility to a team member.'}
        </Typography>
      </InfoBanner>
      <EligibilityRouteToField step={step} onUpdate={onUpdate} isMedbillClient={isMedbillClient} />
      {isMedbillClient && (
        <DescriptionText>
          When a case is routed to the queue, billers complete a structured form capturing all
          benefit details. Verified data flows back into the patient&apos;s Myriad eligibility
          record automatically.
        </DescriptionText>
      )}
    </>
  );
};

const renderEligibilityStepContent = ({ step, onUpdate }) => {
  if (step.field === ELIGIBILITY_STEP_FIELDS.CHECK_270_271) {
    return <Check270271Content />;
  }

  if (step.field === ELIGIBILITY_STEP_FIELDS.ALTERNATE_CLEARINGHOUSE_AI) {
    return (
      <DescriptionText>
        If the primary check fails or returns incomplete data, Myriad routes the request through a
        secondary clearinghouse and layers in AI-aggregated payer data to fill coverage gaps before
        escalating.
      </DescriptionText>
    );
  }

  if (step.field === ELIGIBILITY_STEP_FIELDS.ALERT_BILLER) {
    return <AlertBillerContent step={step} onUpdate={onUpdate} />;
  }

  if (COMING_SOON_FIELDS.includes(step.field)) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          {step.description && <DescriptionText>{step.description}</DescriptionText>}
          {step.pricing && (
            <div className="flex items-center gap-1">
              <DescriptionText weight="semibold">{step.pricing}</DescriptionText>
              <DescriptionText> when enabled.</DescriptionText>
            </div>
          )}
        </div>

        <WaitlistCard featureKey={step.field} />
      </div>
    );
  }

  return null;
};

export default renderEligibilityStepContent;
